import type { CalibrationProposal } from "@sitescene/types";

import { useState } from "react";

import { type Column, DataTable } from "@/components/data-table";
import { StatusMessage } from "@/components/status-message";
import { Button } from "@/components/ui/buttons/button";
import { NativeSelect } from "@/components/ui/select/select-native";
import {
	useCalibrationProposals,
	useReviewProposal,
	useRunCalibration,
} from "@/features/plausibility/use-calibration";

import { formatLogOdds, formatWhen, logOddsToPercent } from "./format";

const STATUSES = ["proposed", "accepted", "rejected"];

/**
 * What the verdicts say the tables should be. Proposals are computed across
 * every participating organization; accepting one writes the table and bumps
 * its version, which is the only way calibration data ever reaches the
 * verifier.
 */
export const Calibration = () => {
	const [status, setStatus] = useState("proposed");
	const proposals = useCalibrationProposals(status);
	const review = useReviewProposal();
	const run = useRunCalibration();
	const rows = proposals.data ?? [];

	const columns: Column<CalibrationProposal>[] = [
		{ header: "Kind", key: "kind", render: (p) => p.kind },
		{ header: "Target", key: "target", render: describeTarget },
		{
			header: "Current → proposed",
			key: "values",
			render: (p) =>
				p.proposed === null ? (
					<span className="text-tertiary">informational</span>
				) : (
					<span>
						{formatValue(p, p.current)} →{" "}
						<strong>{formatValue(p, p.proposed)}</strong>
					</span>
				),
		},
		{
			header: "Support",
			key: "support",
			render: (p) => `${p.support.positives} / ${p.support.n}`,
		},
		{ header: "Recommendation", key: "rec", render: (p) => p.recommendation },
		{
			header: "Metrics",
			key: "metrics",
			render: (p) => (
				<span className="text-xs text-secondary">
					{p.metrics
						? Object.entries(p.metrics)
								.map(([k, v]) => `${k} ${v}`)
								.join(" · ")
						: ""}
				</span>
			),
		},
		{
			header: "Computed",
			key: "computed",
			render: (p) => formatWhen(p.computedAt),
		},
		{
			header: "",
			key: "actions",
			render: (p) =>
				p.status === "proposed" && p.proposed !== null ? (
					<span className="flex gap-2">
						<Button
							isDisabled={
								review.isPending || p.recommendation === "insufficient"
							}
							onClick={() =>
								review.mutate({ action: "accept", proposalId: p.id })
							}
							size="sm"
							type="button"
						>
							Accept
						</Button>
						<Button
							color="primary-destructive"
							isDisabled={review.isPending}
							onClick={() =>
								review.mutate({ action: "reject", proposalId: p.id })
							}
							size="sm"
							type="button"
						>
							Reject
						</Button>
					</span>
				) : p.reviewedAt ? (
					<span className="text-xs text-tertiary">
						{p.status} {formatWhen(p.reviewedAt)}
					</span>
				) : null,
		},
	];

	return (
		<div>
			<div className="flex flex-wrap items-end justify-between gap-3">
				<NativeSelect
					label="Status"
					onChange={(e) => setStatus(e.target.value)}
					options={STATUSES.map((s) => ({ label: s, value: s }))}
					value={status}
				/>
				<div className="flex items-center gap-3">
					{run.data && (
						<span className="text-sm text-secondary">
							{run.data.samples} samples from {run.data.tenants} organization(s)
							→ {run.data.proposals} proposals
						</span>
					)}
					<Button
						color="secondary"
						isDisabled={run.isPending}
						onClick={() => run.mutate()}
						size="sm"
						type="button"
					>
						{run.isPending ? "Running…" : "Run calibration now"}
					</Button>
				</div>
			</div>
			{(review.error || run.error) && (
				<p className="mt-2 text-sm text-error-primary" role="alert">
					{(review.error ?? run.error)?.message}
				</p>
			)}
			<div className="mt-4">
				<StatusMessage
					empty="No proposals. Record verdicts, then run calibration."
					error={proposals.error}
					isEmpty={!proposals.isLoading && rows.length === 0}
					isLoading={proposals.isLoading}
					loading="Loading proposals…"
				/>
				{rows.length > 0 && (
					<DataTable columns={columns} rowKey={(p) => p.id} rows={rows} />
				)}
			</div>
		</div>
	);
};

const describeTarget = (p: CalibrationProposal) => {
	switch (p.kind) {
		case "anchoredModel":
			return `${p.classId ?? ""} · ${p.feature === "*" ? "all features" : (p.feature ?? "")} · real vs shadow model`;
		case "decisionQuality":
			return p.classId ?? "";
		case "lrFeature":
			return `${p.classId ?? ""} · ${p.feature ?? ""}`;
		case "lrStrength":
			return `strength: ${p.strength ?? ""}`;
		case "prior":
			return `${p.classId ?? ""} · ${p.season ?? "any season"} · ${p.hourBucket ?? "any hour"}`;
	}
};

const formatValue = (p: CalibrationProposal, value: null | number) => {
	if (value === null) return "—";
	return p.kind === "prior"
		? `${formatLogOdds(value)} (${logOddsToPercent(value)})`
		: formatLogOdds(value);
};
